import { useCallback, useEffect, useRef, useState } from 'react'
import { connectorSyncsOnDemand } from '@app/shared'
import { useQueryClient } from '@tanstack/react-query'
import type { ISODate, Task } from '@app/shared'
import clsx from 'clsx'

import { useClock, useSyncConnector, useTasks, useToggleTask } from '../../lib/data'
import {
  buildBoard,
  dayGroupLabel,
  doneDays,
  duePill,
  lensOwnsOverdue,
  type TaskBoard,
} from '../../components/tasks/derive'
import { MCheck, MChevronRight } from '../shell/icons'
import { ABOVE_TAB_BAR, Band, BandHeading, Chip, ChipRow, SCROLL_BOTTOM, Skeleton } from '../shell/parts'
import { PullToRefresh } from '../sync/PullToRefresh'
import { useSyncHonesty } from '../sync/SyncReport'
import { moveSwipe, releaseSwipe, shouldCommit, UNDO_MS, NO_SWIPE, beginSwipe, type SwipeState } from '../lib/swipe'

// ============================================================================
// 1g · TASKS — docs/design/handoff-mobile/README.md §1g.
//
// "Replace the phone's reminders app."
//
// ── WHAT NO GATE CAN SEE, and the reason this screen exists in this form:
// THE SWIPE MUST TRACK THE FINGER, NOT ANIMATE ON RELEASE. The row's transform
// is the pointer's own delta, 1:1, with a transition applied ONLY once the finger
// is up (`--dur-swipe`, 180ms). A row that eases toward the finger reads as lag;
// a row that snaps to +96px on release reads as a button that was pressed. The
// arithmetic is in mobile/lib/swipe.ts and is unit-tested; the FEEL is not, and
// cannot be, so it is named here and in the port report.
//
// ── AND WHAT HAPPENS AFTERWARDS IS NOTHING. "Completion moves the row to the
// done log and nothing else happens — no animation flourish, no streak, no
// percentage, no toast beyond a 4-second undo affordance." That absence is
// asserted in the smoke test, so a later edit that adds a ring fails a run rather
// than a review.
// ============================================================================

/** The four lenses, in the mockup's order. */
type Lens = 'today' | 'week' | 'someday' | 'inbox'

const LENSES: Array<{ id: Lens; label: string }> = [
  { id: 'today', label: 'Today' },
  { id: 'week', label: 'This week' },
  { id: 'someday', label: 'Someday' },
  { id: 'inbox', label: 'Inbox' },
]

export function MobileTasks() {
  const queryClient = useQueryClient()
  const { data: clock } = useClock()
  const { data: tasks, isPending, error } = useTasks()
  const toggle = useToggleTask()
  const sync = useSyncConnector()
  const honesty = useSyncHonesty(clock?.date, clock?.minutesIntoDay ?? 0)

  const [lens, setLens] = useState<Lens>('today')
  const [logOpen, setLogOpen] = useState(false)
  /** The completion that can still be taken back. Cleared after four seconds. */
  const [undo, setUndo] = useState<{ id: string; title: string } | null>(null)
  const undoTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => () => {
    if (undoTimer.current) clearTimeout(undoTimer.current)
  }, [])

  const complete = useCallback(
    (task: Task) => {
      toggle.mutate({ id: task.id, done: true })
      setUndo({ id: task.id, title: task.title })
      if (undoTimer.current) clearTimeout(undoTimer.current)
      undoTimer.current = setTimeout(() => setUndo(null), UNDO_MS)
    },
    [toggle],
  )

  const refresh = useCallback(async () => {
    const live = (honesty.connectors ?? []).filter(connectorSyncsOnDemand)
    await Promise.allSettled(live.map((c) => sync.mutateAsync(c.id)))
    await queryClient.invalidateQueries()
  }, [honesty.connectors, queryClient, sync])

  const board = clock && tasks ? buildBoard(tasks, clock.date) : null
  const log = clock && tasks ? doneDays(tasks, clock.date) : []
  const doneToday = log.find((d) => d.date === clock?.date)?.tasks.length ?? 0

  return (
    <>
      <Band band="tasks">
        <BandHeading
          title="Reminders"
          subline={
            board ? (
              <span>
                {board.openCount} open
                {board.overdue.length > 0 && ` · ${board.overdue.length} overdue`}
              </span>
            ) : undefined
          }
        />
      </Band>

      <ChipRow>
        {LENSES.map((l) => (
          <Chip key={l.id} active={lens === l.id} onClick={() => setLens(l.id)}>
            {l.label}
          </Chip>
        ))}
      </ChipRow>

      <PullToRefresh connectors={honesty.connectors} onRefresh={refresh}>
        <div
          // `pt-[6px]`: the chip row ends 10px above this column and the lens heading sat hard
          // against it (owner, 2026-09-12: "way too little space between the top bar and Today").
          // 16px total is the same breath the band gives the chips above.
          className="flex flex-col gap-[12px] px-[14px] pt-[6px]"
          style={{ paddingBottom: SCROLL_BOTTOM }}
        >
          {isPending && (
            <>
              <Skeleton height={62} className="rounded-card" />
              <Skeleton height={62} className="rounded-card" />
              <Skeleton height={62} className="rounded-card" />
            </>
          )}

          {error && (
            <p className="text-[12px] leading-[1.5] text-microcopy text-pretty">
              The task list could not be read, so none is shown — an empty board here would say you
              have nothing to do. {error instanceof Error ? error.message : ''}
            </p>
          )}

          {board && clock && (
            <>
              {/* OVERDUE IS PINNED TO THE TOP OF EVERY LENS. It is not the Today
                  lens's private problem, and softening it into a bigger number
                  is the thing this screen refuses. */}
              {board.overdue.length > 0 && (
                <Group
                  label="Overdue"
                  count={board.overdue.length}
                  tasks={board.overdue}
                  today={clock.date}
                  overdue
                  onComplete={complete}
                />
              )}

              {/* `emptyIsNews` is false for Today while something is overdue: the group above is
                  already showing late work, and "Nothing here" printed under it would be the
                  screen contradicting itself in two consecutive paragraphs. Overdue's home is
                  Today (components/tasks/derive.ts → lensOwnsOverdue). */}
              <Group
                label={LENSES.find((l) => l.id === lens)!.label}
                count={lensTasks(board, lens).length}
                tasks={lensTasks(board, lens)}
                today={clock.date}
                sayEmpty={!(lensOwnsOverdue(lens) && board.overdue.length > 0)}
                onComplete={complete}
              />

              {/* THE DONE LOG — one collapsed row that expands in place. */}
              <div className="flex flex-none flex-col overflow-hidden rounded-card border border-fill bg-surface-2">
                <button
                  type="button"
                  aria-expanded={logOpen}
                  onClick={() => setLogOpen((open) => !open)}
                  className="flex items-center gap-[11px] px-[14px] py-[13px] active:bg-fill"
                >
                  <span className="text-[12.5px] font-semibold leading-none text-ink-3">
                    Done log
                  </span>
                  <span className="text-[11px] font-normal leading-none text-microcopy">
                    {doneToday} today
                  </span>
                  <span
                    className={clsx(
                      'ml-auto text-faintest transition-transform duration-[var(--dur-fast)] ease-[var(--ease-out)]',
                      logOpen && 'rotate-90',
                    )}
                  >
                    <MChevronRight />
                  </span>
                </button>
                {logOpen && (
                  <div className="flex flex-col gap-3 border-t border-fill px-[14px] pb-[13px] pt-[11px]">
                    {log.length === 0 && (
                      <p className="text-[11.5px] leading-[1.4] text-microcopy">
                        Nothing completed yet.
                      </p>
                    )}
                    {log.slice(0, 5).map((day) => (
                      <div key={day.date} className="flex flex-col gap-[6px]">
                        <span className="text-[10px] font-semibold uppercase leading-none tracking-[0.07em] text-faint">
                          {dayGroupLabel(day.date, clock.date)}
                        </span>
                        {day.tasks.map((t) => (
                          <span
                            key={t.id}
                            className="text-[12.5px] font-medium leading-[1.3] text-ink-3 text-pretty"
                          >
                            {t.title}
                          </span>
                        ))}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </PullToRefresh>

      {/* THE ONLY THING THAT HAPPENS AFTER A COMPLETION: four seconds, one
          action, no celebration. It sits above the tab bar rather than over it,
          so the thumb that just swiped is not on top of the way back. */}
      {undo && (
        <div
          role="status"
          className="pointer-events-none absolute inset-x-[14px] flex justify-center"
          style={{ bottom: ABOVE_TAB_BAR }}
        >
          <div className="pointer-events-auto flex max-w-full items-center gap-3 rounded-card bg-ink px-[14px] py-[11px] shadow-card">
            <span className="min-w-0 truncate text-[12.5px] font-medium leading-none text-surface">
              Done · {undo.title}
            </span>
            <button
              type="button"
              onClick={() => {
                toggle.mutate({ id: undo.id, done: false })
                setUndo(null)
              }}
              className="flex-none rounded-chip text-[12.5px] font-semibold leading-none text-today-lift underline underline-offset-2 active:bg-ink-2"
            >
              Undo
            </button>
          </div>
        </div>
      )}
    </>
  )
}

function lensTasks(board: TaskBoard, lens: Lens): Task[] {
  switch (lens) {
    case 'today':
      return board.dueToday
    case 'week':
      return board.soon
    case 'someday':
      return board.someday
    case 'inbox':
      return board.inbox
  }
}

function Group({
  label,
  count,
  tasks,
  today,
  overdue,
  sayEmpty = true,
  onComplete,
}: {
  label: string
  count: number
  tasks: Task[]
  today: ISODate
  overdue?: boolean
  /** False when another group on screen is already answering — see the Today lens's note. */
  sayEmpty?: boolean
  onComplete: (task: Task) => void
}) {
  return (
    <>
      <div className="flex flex-none items-baseline gap-[9px] pl-[2px]">
        <span className="text-[12.5px] font-semibold leading-none text-ink">{label}</span>
        <span className="text-[11px] font-normal leading-none text-microcopy">
          {count} {count === 1 ? 'item' : 'items'}
        </span>
      </div>

      {tasks.length === 0 && sayEmpty && (
        <p className="flex-none pl-[2px] text-[11.5px] leading-[1.4] text-microcopy text-pretty">
          Nothing here. That is the lens being empty, not the list failing to load.
        </p>
      )}

      {tasks.map((task) => (
        <SwipeRow
          key={task.id}
          task={task}
          today={today}
          overdue={Boolean(overdue)}
          onComplete={() => onComplete(task)}
        />
      ))}
    </>
  )
}

/**
 * ONE TASK ROW, on a `success-fill` bed with a check and the word "Done" at 18px
 * from the left edge — so the thing the gesture is going to do is visible from
 * the first millimetre of movement rather than only after it commits.
 *
 * The checkbox is still a checkbox. A gesture is not an accessible control, and
 * a screen with only a swipe would be a screen a keyboard or a screen reader
 * cannot finish a task on.
 */
function SwipeRow({
  task,
  today,
  overdue,
  onComplete,
}: {
  task: Task
  today: ISODate
  overdue: boolean
  onComplete: () => void
}) {
  const [swipe, setSwipe] = useState<SwipeState>(NO_SWIPE)
  const [settling, setSettling] = useState(false)
  const from = useRef<number | null>(null)
  const box = useRef<HTMLDivElement>(null)

  const width = box.current?.offsetWidth ?? 0
  const armed = shouldCommit(swipe, width)
  const pill = duePill(task, today)

  return (
    <div ref={box} className="relative flex-none overflow-hidden rounded-card">
      {/* The bed. Visible the moment the row starts to move. */}
      <div className="absolute inset-0 flex items-center gap-[10px] bg-success-fill pl-[18px]">
        <MCheck size={20} className="text-success" />
        <span className="text-[13px] font-semibold leading-none text-success">Done</span>
      </div>

      <div
        className={clsx(
          // `select-none`: a horizontal drag across a row of text otherwise
          // starts a text selection, which fights the gesture and leaves a blue
          // highlight behind it. `touch-pan-y` keeps vertical scrolling with the
          // list while the horizontal axis belongs to this handler.
          'relative flex touch-pan-y select-none items-center gap-3 rounded-card border bg-surface px-[14px] py-[13px]',
          overdue ? 'border-danger-line' : 'border-line',
          swipe.offset > 0 && 'shadow-swipe',
        )}
        style={{
          transform: `translateX(${swipe.offset}px)`,
          // THE TRANSITION IS ONLY EVER PRESENT AFTER THE FINGER IS UP. While it
          // is down the transform is the pointer's own delta with nothing
          // between them — that is what 1:1 means, and a transition here is
          // exactly the lag the README's closing section warns about.
          transition: settling ? `transform var(--dur-swipe) var(--ease-out)` : undefined,
        }}
        onPointerDown={(e) => {
          from.current = e.clientX
          setSettling(false)
          setSwipe(beginSwipe(task.id))
          e.currentTarget.setPointerCapture(e.pointerId)
        }}
        onPointerMove={(e) => {
          if (from.current === null) return
          setSwipe((s) => moveSwipe(s, task.id, e.clientX - from.current!, width))
        }}
        onPointerUp={() => {
          if (from.current === null) return
          from.current = null
          const { commit } = releaseSwipe(swipe, width)
          setSettling(true)
          setSwipe(NO_SWIPE)
          if (commit) onComplete()
        }}
        onPointerCancel={() => {
          from.current = null
          setSettling(true)
          setSwipe(NO_SWIPE)
        }}
      >
        <button
          type="button"
          aria-label={`Complete ${task.title}`}
          onClick={onComplete}
          className={clsx(
            'flex size-6 flex-none items-center justify-center rounded-chip border-[1.8px] active:bg-fill',
            overdue ? 'border-danger-dot' : 'border-dash',
            armed && 'border-success-dot',
          )}
        >
          {armed && <MCheck size={14} className="text-success-dot" />}
        </button>

        <div className="min-w-0 flex-1">
          <div className="text-[15px] font-semibold leading-[1.25] text-ink text-pretty">
            {task.title}
          </div>
          {pill && (
            <div
              className={clsx(
                'mt-[5px] text-[11.5px] leading-none',
                overdue ? 'font-medium text-danger-2' : 'font-normal text-muted',
              )}
            >
              {overdue ? `was due ${pill}` : pill}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
